import { Check, Download, Eye, FileText, Loader2, Pencil, Trash2, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { fileKindLabel, isPreviewable } from '@/features/documents/utils/file-kind'
import { cn } from '@/shared/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle
} from '@/shared/ui/attachment'
import { Input } from '@/shared/ui/input'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip'
import { formatBytes } from '@/shared/utils/handleData'

export type CreditDocumentCardDocument = {
  id: string
  name: string
  size: number
  mimeType: string | null
}

type CreditDocumentCardProps = {
  document: CreditDocumentCardDocument
  /**
   * La línea de contexto: de qué etapa y qué campo salió el archivo.
   *
   * Opcional porque las dos pantallas la necesitan distinto. Dentro del campo del checklist el
   * contexto es la pantalla misma —repetirlo sería ruido—; en la pestaña «Documentos», donde
   * conviven los archivos de siete etapas, es lo único que dice de dónde salió cada uno.
   */
  context?: string
  /**
   * ☠️ **`false` no esconde las acciones: las DESHABILITA con su motivo.**
   *
   * Es la regla que el repo ya aplica al botón de publicar del cartapacio. Esconder un botón
   * enseña que la acción no existe; verlo apagado con un tooltip enseña que existe y por qué hoy
   * no se puede — y acá el motivo es información de negocio («este trámite ya se aprobó»), no un
   * detalle técnico.
   */
  canWrite?: boolean
  /** Por qué no se puede escribir. Se pinta en el tooltip de las acciones apagadas. */
  writeBlockedReason?: string
  downloading?: boolean
  renaming?: boolean
  removing?: boolean
  onDownload: (id: string) => void
  onRename: (id: string, name: string) => void
  onRemove: (id: string) => void
  className?: string
}

/**
 * La tarjeta de UN documento del expediente de crédito.
 *
 * ── DE DÓNDE SALE LA FORMA ───────────────────────────────────────────────────
 *
 * Del molde que el lead nombró: la card de adjuntos de negociaciones y clientes
 * (`features/attachments/.../file-card.tsx`) — ícono por tipo, nombre, tipo · tamaño, y a la
 * derecha ver/descargar, renombrar y eliminar. Lo que cambia es el ARMADO: se construye sobre la
 * familia `Attachment` de shadcn (`@/shared/ui/attachment`), que es la que ya usa el cartapacio
 * v2 (`document-row.tsx`), en vez de repetir el `div` con clases sueltas de la card vieja. Misma
 * experiencia, primitivo del repo.
 *
 * ── VER Y DESCARGAR SON UN SOLO BOTÓN ────────────────────────────────────────
 *
 * Y muta de ícono: ojo cuando el navegador puede pintarlo (PDF, imagen), flecha cuando lo va a
 * bajar. Es lo que hace la card de negociaciones, y es honesto porque **del lado del servidor
 * también es una sola llamada**: el `Content-Disposition` lo decide el MIME. Dos botones
 * separados prometerían una diferencia que el backend no hace.
 *
 * ── RENOMBRAR ES EN LÍNEA, NO EN UN DIÁLOGO ──────────────────────────────────
 *
 * La card vieja abre un diálogo de formulario completo (nombre + comentario + carpeta) porque ahí
 * el mismo diálogo también CREA el adjunto. Acá lo único editable es el nombre y la tarjeta vive
 * dentro de un panel lateral angosto: un diálogo modal por corregir un typo es un paso de más y
 * tapa el expediente que se está mirando.
 */
export function CreditDocumentCard({
  document,
  context,
  canWrite = false,
  writeBlockedReason,
  downloading = false,
  renaming = false,
  removing = false,
  onDownload,
  onRename,
  onRemove,
  className
}: CreditDocumentCardProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(document.name)
  const [confirmingRemove, setConfirmingRemove] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // El borrador se resincroniza cuando el servidor devuelve otro nombre —el propio renombre, o
  // el de otra persona sobre el mismo expediente—. Sin esto, cancelar después de un refetch
  // dejaría escrito en pantalla un nombre que ya no es el del archivo.
  useEffect(() => {
    if (!editing) setDraft(document.name)
  }, [document.name, editing])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const pending = downloading || renaming || removing
  const trimmed = draft.trim()
  const canSubmitRename = trimmed.length > 0 && trimmed !== document.name

  const commitRename = () => {
    if (!canSubmitRename) {
      setEditing(false)
      setDraft(document.name)
      return
    }
    onRename(document.id, trimmed)
    setEditing(false)
  }

  const previewable = isPreviewable(document.mimeType)

  return (
    <>
      <Attachment
        orientation="horizontal"
        state={pending ? 'uploading' : 'done'}
        size="sm"
        className={cn('w-full flex-nowrap', className)}
        aria-busy={pending}
      >
        <AttachmentMedia>
          <FileText aria-hidden="true" />
        </AttachmentMedia>

        <AttachmentContent>
          {editing ? (
            <Input
              ref={inputRef}
              value={draft}
              aria-label={`Nombre de ${document.name}`}
              className="h-7 text-xs"
              maxLength={200}
              onChange={(event) => {
                setDraft(event.target.value)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  commitRename()
                }
                if (event.key === 'Escape') {
                  event.preventDefault()
                  setEditing(false)
                  setDraft(document.name)
                }
              }}
            />
          ) : (
            <AttachmentTitle title={document.name}>{document.name}</AttachmentTitle>
          )}
          <AttachmentDescription>
            {fileKindLabel(document.mimeType)} · {formatBytes(document.size)}
            {context ? ` · ${context}` : ''}
          </AttachmentDescription>
        </AttachmentContent>

        <AttachmentActions className="gap-0.5">
          {editing ? (
            <>
              <AttachmentAction
                size="icon-sm"
                aria-label="Guardar el nombre"
                disabled={!canSubmitRename || renaming}
                onClick={commitRename}
              >
                <Check className="size-4" />
              </AttachmentAction>
              <AttachmentAction
                size="icon-sm"
                aria-label="Cancelar"
                onClick={() => {
                  setEditing(false)
                  setDraft(document.name)
                }}
              >
                <X className="size-4" />
              </AttachmentAction>
            </>
          ) : (
            <>
              {/* SIEMPRE disponible, también en un expediente aprobado: la regla es «escritura
                  mientras dura el proceso, lectura siempre». Un trámite cerrado es justamente
                  cuando más se consultan sus papeles. */}
              <AttachmentAction
                size="icon-sm"
                aria-label={`${previewable ? 'Ver' : 'Descargar'} ${document.name}`}
                disabled={downloading}
                onClick={() => {
                  onDownload(document.id)
                }}
              >
                {downloading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : previewable ? (
                  <Eye className="size-4" />
                ) : (
                  <Download className="size-4" />
                )}
              </AttachmentAction>

              <WriteAction
                canWrite={canWrite}
                reason={writeBlockedReason}
                label={`Cambiar el nombre de ${document.name}`}
                disabled={pending}
                onClick={() => {
                  setEditing(true)
                }}
              >
                <Pencil className="size-4" />
              </WriteAction>

              <WriteAction
                canWrite={canWrite}
                reason={writeBlockedReason}
                label={`Eliminar ${document.name}`}
                disabled={pending}
                onClick={() => {
                  setConfirmingRemove(true)
                }}
              >
                {removing ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Trash2 className="size-4" />
                )}
              </WriteAction>
            </>
          )}
        </AttachmentActions>
      </Attachment>

      {/* Borrar un adjunto no se deshace, y además desprende la referencia del campo que lo
          pedía: se confirma. */}
      <AlertDialog open={confirmingRemove} onOpenChange={setConfirmingRemove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borrará «{document.name}» y el campo del expediente quedará sin archivo. Esta
              acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onRemove(document.id)
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

/**
 * Una acción de ESCRITURA: se ve siempre, y cuando no se puede usar explica por qué.
 *
 * `aria-disabled` y no `disabled` en la rama bloqueada: un botón deshabilitado de verdad sale del
 * orden de tabulación, y con él se va la única explicación de por qué no se puede. Es el mismo
 * tratamiento que `document-row.tsx` le da al botón de publicar sin permiso.
 */
function WriteAction({
  canWrite,
  reason,
  label,
  disabled,
  onClick,
  children
}: {
  canWrite: boolean
  reason?: string
  label: string
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  if (canWrite) {
    return (
      <AttachmentAction size="icon-sm" aria-label={label} disabled={disabled} onClick={onClick}>
        {children}
      </AttachmentAction>
    )
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <AttachmentAction
            size="icon-sm"
            aria-label={label}
            aria-disabled
            className="opacity-40"
            onClick={(event) => {
              event.preventDefault()
            }}
          >
            {children}
          </AttachmentAction>
        </TooltipTrigger>
        <TooltipContent className="max-w-56">
          {reason ?? 'No tienes permiso para modificar los documentos de este expediente.'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
